import { FormControl, FormHelperText, FormLabel, TextField } from "@mui/material";
import { Formik } from "formik";
import moment from "moment";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import AppButton from "@/common/components/Button/AppButton";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import ApiEnumAutocomplete from "@/common/components/Enum/ApiEnumAutocomplete";
import GeneralValidationError from "@/common/components/Error/GeneralValidationError";
import FileUploader from "@/common/components/Files/FileUploader";
import FormActions from "@/common/components/Form/FormActions";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ApiEnumName } from "@/common/services/enum";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { PersonalDataDeleteDto, PersonalDataDeletionReasonType } from "@/core/api/generated/v1";

export type PersonalDataDeleteValues = PersonalDataDeleteDto &
  BaseFormikValues & {
    isAttachmentFilesUploading?: boolean;
    isAttachmentFilesHaveErrors?: boolean;
  };

export interface PersonalDataDeleteProps {
  defaultValues?: Partial<PersonalDataDeleteValues>;
  onCancel: () => void;
  /** When form is submitted and API request should be sent. */
  onSave: (dto: PersonalDataDeleteDto) => Promise<void>;
}

export function PersonalDataDelete({ defaultValues, onCancel, onSave }: PersonalDataDeleteProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();

  return (
    <Formik<PersonalDataDeleteValues>
      enableReinitialize
      initialValues={{
        reasonType: defaultValues?.reasonType || PersonalDataDeletionReasonType.CustomerInquiry,
        reason: defaultValues?.reason || undefined,
        inquiryDate: defaultValues?.inquiryDate || undefined,
        inquiryAttachments: defaultValues?.inquiryAttachments || undefined,
        submit: "",
      }}
      onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
        try {
          const values2: typeof values = {
            ...values,
            inquiryDate: values.inquiryDate ? moment(values.inquiryDate).utc().format() : undefined,
          };
          await onSave(values2);
          enqueueSnackbar("Personal data deleted.", { variant: "success" });

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err: any) {
          if (mounted.current) {
            ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => {
        return (
          <form noValidate onSubmit={handleSubmit}>
            <FormContentBlock>
              <FormControl margin='dense' fullWidth>
                <ApiEnumAutocomplete
                  type={ApiEnumName.PersonalDataDeletionReasonType}
                  value={values.reasonType}
                  label='Reason type'
                  required
                  textFieldProps={{
                    error: Boolean(errors.reasonType),
                  }}
                  onChange={(newValue) => setFieldValue("reasonType", newValue)}
                />
                <FormHelperText>
                  {errors.reasonType && ValidationHelper.getErrorsAsString(errors.reasonType)}
                </FormHelperText>
              </FormControl>

              <TextField
                label='Reason text'
                fullWidth
                required={values.reasonType === PersonalDataDeletionReasonType.Other}
                type='text'
                variant='outlined'
                margin='dense'
                name='reason'
                value={values.reason}
                error={Boolean(touched.reason && errors.reason)}
                helperText={
                  (touched.reason &&
                    errors.reason &&
                    ValidationHelper.getErrorsAsString(errors.reason)) ||
                  undefined
                }
                onBlur={handleBlur}
                onChange={handleChange}
              />

              <FormControl margin='dense' fullWidth>
                <AppDateTimePicker
                  label='Inquiry date'
                  value={(values.inquiryDate && moment(values.inquiryDate)) || null}
                  format={DATETIME_FORMATS.DISPLAY_DATETIME}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.inquiryDate),
                      helperText:
                        (errors.inquiryDate &&
                          ValidationHelper.getErrorsAsString(errors.inquiryDate)) ||
                        undefined,
                    },
                  }}
                  onChange={(newValue) => {
                    setFieldValue("inquiryDate", newValue?.format() || null);
                  }}
                />
              </FormControl>

              <FormControl fullWidth component='fieldset' margin='dense'>
                <FormLabel sx={{ mb: 0.5 }}>Inquiry attachments</FormLabel>

                <FileUploader
                  multiple
                  maxFiles={10}
                  defaultFiles={FileItem.createManyFrom(values.inquiryAttachments)}
                  onChange={(newFiles) => {
                    setFieldValue(
                      `inquiryAttachments`,
                      FileItem.toManyGeneralAttachmentInputDto(newFiles),
                    );
                  }}
                  onUploadStarted={() => {
                    setFieldValue("isAttachmentFilesUploading", true);
                  }}
                  onUploadFinished={() => {
                    setFieldValue("isAttachmentFilesUploading", false);
                  }}
                  onValidationStatusChange={(filesValidationStatus) => {
                    if (filesValidationStatus)
                      setFieldValue(
                        "isAttachmentFilesHaveErrors",
                        Object.values(filesValidationStatus).some((x) => x === false),
                      );
                  }}
                />
              </FormControl>
            </FormContentBlock>

            <GeneralValidationError sx={{ my: 1 }} errors={errors} />

            <FormActions>
              <AppButton
                color='primary'
                loading={isSubmitting}
                fullWidth
                type='submit'
                variant='contained'
              >
                Save
              </AppButton>

              <AppButton
                color='text'
                disabled={isSubmitting}
                fullWidth
                type='button'
                variant='outlined'
                onClick={onCancel}
              >
                Cancel
              </AppButton>
            </FormActions>
          </form>
        );
      }}
    </Formik>
  );
}
